import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Slide,
  Snackbar,
  Typography,
  Container,
} from '@mui/material';
import useEvent from '../../utils/useEvent';
import successOutlined from '../../img/success-outlined.svg';
import { useCurrentUser } from '../../context/UserContext';
import EventDetails from './EventDetails';
import CopyLinkTutorial from './CopyLinkTutorial';
import RSVPForm from './RSVPForm';
import CopyLinkButton from '../Controls/CopyLinkButton';
import { EVENTS, PAGE_HEIGHT, SNACKBAR_DURATION } from '../../constants';
import PageLoader from '../PageLoader';

const styles = {
  button: {
    marginBottom: '20px',
  },
  snackbar: {
    bottom: 0,
    height: '128px',
    left: 0,
    maxWidth: '512px',
    right: 0,
    width: '100%',
  },
  snackbarContent: {
    backgroundColor: (theme) => theme.palette.primary.main,
    height: '100%',
    padding: '20px 0',
    width: '100%',
  },
  addToCalendar: {
    backgroundColor: (theme) => theme.palette.primary.main,
    padding: '20px',
  },
  copyButton: {
    width: '100%'
  }
};

export default function Event() {
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  const { user } = useCurrentUser();

  const [snackbarOpen, setSnackbarOpen] = useState(null);
  const [snackbarTransition] = useState(Slide);
  const [locationState] = useState(location.state || {});

  const { fromCreate, fromUpdate } = locationState;

  useEffect(() => {
    setSnackbarOpen(fromCreate || fromUpdate);
  }, [fromCreate, fromUpdate]);

  useEffect(() => {
    // snackbarOpen is null unless explicitly set to false,
    // either through fromCreate/fromUpdate being false, or
    // the snackbar closing
    if (snackbarOpen === false && (fromCreate || fromUpdate)) {
      // Once snackbar is closed, replace the state so we don't
      // show the snackbar on refresh
      navigate(
        {
          ...navigate,
          state: { ...navigate.state, fromCreate: false, fromUpdate: false },
        },
        { replace: true }
      );
    }
  }, [snackbarOpen, fromCreate, fromUpdate, navigate]);

  const {
    data: event,
    isLoading: eventLoading,
    refetch: refetchEvent,
  } = useEvent(id);

  if (eventLoading) {
    return <PageLoader />;
  }

  if (!event) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        style={{ height: PAGE_HEIGHT, paddingTop: '200px' }}
      >
        <Typography variant="h2" color="primary">
          Event not found.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={() => {
            navigate('/events/create');
          }}
        >
          Create Event
        </Button>
      </Box>
    );
  }

  function handleSnackbarClose() {
    setSnackbarOpen(false);
  }

  let snackbarMessage = '';
  if (fromCreate) {
    snackbarMessage = `Congrats! ${event.title} is now live.`;
  } else if (fromUpdate) {
    snackbarMessage = `${event.title} successfully updated!`;
  }

  const isOwner =
    event.users.find((u) => u.id === user?._id)?.role === EVENTS.ROLE.OWNER;
  return (
    <Box>
      {isOwner && fromCreate && <CopyLinkTutorial />}
      {isOwner && (
        <Container>
          <CopyLinkButton
            beforeText="Copy Event Link"
            afterText="Link Copied to Clipboard!"
            url={document.location.href}
            sx={styles.copyButton}
          />
        </Container>
      )}
      <EventDetails event={event} editable={isOwner} />
      {event.rsvp && <RSVPForm event={event} refetchEvent={refetchEvent} />}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={SNACKBAR_DURATION}
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        TransitionComponent={snackbarTransition}
        sx={{ root: styles.snackbar }}
      >
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="space-around"
          sx={styles.snackbarContent}
        >
          <Typography variant="h4" style={{ color: '#fff' }}>
            {snackbarMessage}
          </Typography>
          <img src={successOutlined} alt="Event created successfully!" />
        </Box>
      </Snackbar>
    </Box>
  );
}
